import { store } from '@/store';
import { fetchCurrencyRates } from '@/store/slices/currencySlice/currencySlice';

class CurrencyService {
  private cache: { [key: string]: { rates: any; timestamp: number; date: string } } = {};
  private cacheExpiry = 86400000; // 24 hours

  private getCurrentDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  async getExchangeRates(): Promise<any> {
    const now = Date.now();
    const currentDate = this.getCurrentDate();

    // Check local cache first
    if (
      this.cache['rates'] &&
      this.cache['rates'].date === currentDate &&
      now - this.cache['rates'].timestamp < this.cacheExpiry
    ) {
      return this.cache['rates'].rates;
    }

    // Check Redux store
    const state = store.getState();
    if (state.currency.rates && state.currency.lastFetched === currentDate) {
      return state.currency.rates.rates;
    }

    // Fetch from backend API
    try {
      await store.dispatch(fetchCurrencyRates());
      const updatedState = store.getState();
      if (updatedState.currency.rates) {
        this.cache['rates'] = {
          rates: updatedState.currency.rates.rates,
          timestamp: now,
          date: currentDate,
        };
        return updatedState.currency.rates.rates;
      }
    } catch (error) {
      console.error('Failed to fetch currency rates:', error);
    }

    return null;
  }

  async convertAmount(amount: number, fromCurrency: string, toCurrency: string): Promise<number> {
    const from = fromCurrency.toUpperCase();
    const to = toCurrency.toUpperCase();

    if (from === to) return amount;

    try {
      const rates = await this.getExchangeRates();
      if (!rates) return amount;

      // Convert from source currency to USD first
      let usdAmount = amount;
      if (from !== 'USD') {
        const fromRate = rates[from];
        if (!fromRate) return amount;
        usdAmount = amount / fromRate;
      }

      // Convert from USD to target currency
      if (to !== 'USD') {
        const toRate = rates[to];
        if (!toRate) return amount;
        return usdAmount * toRate;
      }

      return usdAmount;
    } catch (error) {
      console.warn('Currency conversion failed:', error);
      return amount;
    }
  }

  formatCurrency(amount: number, currency: string): string {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (error) {
      return `${currency.toUpperCase()} ${amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
  }
}

export const currencyService = new CurrencyService();
